import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle,
  Headphones, Mic, BookOpen, PenTool, Target, ChevronDown, ChevronUp,
  ArrowRight, Clock
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOD_CFG = {
  listening: { icon: Headphones, color: "#002FA7", label: "Listening" },
  speaking: { icon: Mic, color: "#3b82f6", label: "Speaking" },
  reading: { icon: BookOpen, color: "#22c55e", label: "Reading" },
  writing: { icon: PenTool, color: "#f59e0b", label: "Writing" },
};
const MODS = ["listening", "speaking", "reading", "writing"];

const TRAJECTORY_CFG = {
  on_track: { label: "On Track", color: "text-green-700", bg: "bg-green-50", border: "border-green-200", icon: CheckCircle },
  at_risk: { label: "At Risk", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", icon: AlertTriangle },
  needs_intervention: { label: "Needs Intervention", color: "text-red-700", bg: "bg-red-50", border: "border-red-200", icon: AlertTriangle },
  growing: { label: "Growing", color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", icon: TrendingUp },
  stable: { label: "Stable", color: "text-gray-600", bg: "bg-gray-50", border: "border-gray-200", icon: Minus },
  declining: { label: "Declining", color: "text-red-600", bg: "bg-red-50", border: "border-red-200", icon: TrendingDown },
};

const TrajectoryBadge = ({ trajectory, size = "sm" }) => {
  const cfg = TRAJECTORY_CFG[trajectory] || TRAJECTORY_CFG.stable;
  const Icon = cfg.icon;
  const textSize = size === "sm" ? "text-[10px]" : "text-xs";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${cfg.bg} ${cfg.border} border ${cfg.color} font-semibold ${textSize}`} data-testid={`trajectory-${trajectory}`}>
      <Icon size={size === "sm" ? 10 : 12} />
      {cfg.label}
    </span>
  );
};

const ProjectionArrow = ({ current, projected, color }) => {
  const diff = projected - current;
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="font-bold" style={{ color }}>{current}</span>
      <ArrowRight size={12} className="text-gray-400" />
      <span className="font-bold" style={{ color }}>{projected}</span>
      <span className={`text-[10px] font-semibold ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>
        ({diff > 0 ? '+' : ''}{diff.toFixed(1)})
      </span>
    </div>
  );
};

const ProjectionGauge = ({ current, projected, goal, color }) => {
  const maxVal = Math.max(goal || 6, projected, current, 6);
  const currentPct = (current / maxVal) * 100;
  const projectedPct = (projected / maxVal) * 100;
  const goalPct = goal ? (goal / maxVal) * 100 : null;

  return (
    <div className="relative h-3 bg-gray-100 overflow-visible mt-1" data-testid="projection-gauge">
      {/* Current */}
      <div className="absolute h-full transition-all" style={{ width: `${currentPct}%`, backgroundColor: color, opacity: 0.4 }} />
      {/* Projected */}
      <div
        className="absolute h-full transition-all"
        style={{
          width: `${projectedPct}%`,
          background: `repeating-linear-gradient(90deg, ${color}33, ${color}33 3px, transparent 3px, transparent 6px)`,
          borderRight: `2px solid ${color}`,
        }}
      />
      {/* Goal marker */}
      {goalPct && (
        <div
          className="absolute top-0 h-full w-0.5 bg-green-500"
          style={{ left: `${goalPct}%` }}
          title={`Goal: ${goal}`}
        >
          <div className="absolute -top-3 -left-1 text-[8px] font-bold text-green-600">G</div>
        </div>
      )}
    </div>
  );
};

const PredictiveProjections = ({ classId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStudent, setExpandedStudent] = useState(null);

  useEffect(() => {
    if (classId) fetchProjections();
  }, [classId]);

  const fetchProjections = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projections/class/${classId}`);
      setData(res.data);
    } catch { toast.error("Failed to load projections"); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="py-12 text-center text-gray-400">Calculating projections...</div>;
  if (!data || !data.students?.length) return <div className="py-12 text-center text-gray-400">No projection data available. Students need at least 2 scored assessments.</div>;

  const { students, summary } = data;
  const tc = summary.trajectory_counts;

  return (
    <div className="space-y-6" data-testid="predictive-projections">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
          <TrendingUp size={20} className="text-[#002FA7]" />
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
            End-of-Year Projections
          </h2>
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <Clock size={12} />
            Projected to {data.projection_target} based on linear growth trajectories
          </p>
        </div>
      </div>

      {/* Trajectory Summary */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="trajectory-summary">
        {Object.entries(TRAJECTORY_CFG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const count = tc[key] || 0;
          return (
            <div key={key} className={`border-2 ${cfg.border} ${cfg.bg} p-3 text-center`} data-testid={`traj-count-${key}`}>
              <Icon size={16} className={`mx-auto mb-1 ${cfg.color}`} />
              <div className={`text-2xl font-bold ${cfg.color}`}>{count}</div>
              <div className="text-[10px] font-semibold text-gray-500">{cfg.label}</div>
            </div>
          );
        })}
      </div>

      {/* Modality Projection Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {MODS.map(mod => {
          const cfg = MOD_CFG[mod];
          const Icon = cfg.icon;
          const mp = summary.modality_projections[mod] || {};
          return (
            <div key={mod} className="border-2 border-gray-200 p-4" data-testid={`mod-projection-${mod}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} style={{ color: cfg.color }} />
                <span className="text-sm font-semibold">{cfg.label}</span>
              </div>
              <div className="text-2xl font-bold" style={{ color: cfg.color }}>
                {mp.avg_projected || '\u2014'}
              </div>
              <div className="text-xs text-gray-500">Avg Projected EOY</div>
              <div className="text-[10px] text-gray-400 mt-1">
                Range: {mp.min_projected}\u2013{mp.max_projected}
              </div>
            </div>
          );
        })}
      </div>

      {/* Student Projection Table */}
      <div className="border-2 border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#002FA7] text-white">
              <th className="text-left p-3 font-semibold">Student</th>
              <th className="text-center p-3 font-semibold">Status</th>
              {MODS.map(mod => (
                <th key={mod} className="text-center p-3 font-semibold">{MOD_CFG[mod].label}</th>
              ))}
              <th className="text-center p-3 font-semibold w-10"></th>
            </tr>
          </thead>
          <tbody>
            {students.map((s, idx) => {
              const isExpanded = expandedStudent === s.student_id;
              return (
                <React.Fragment key={s.student_id}>
                  <tr
                    className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/30 cursor-pointer`}
                    onClick={() => setExpandedStudent(isExpanded ? null : s.student_id)}
                    data-testid={`projection-row-${s.student_id}`}
                  >
                    <td className="p-3">
                      <div className="font-medium">{s.name}</div>
                      {s.has_goals && <span className="text-[9px] text-green-600 font-semibold flex items-center gap-0.5"><Target size={8} /> Goals set</span>}
                    </td>
                    <td className="p-3 text-center">
                      <TrajectoryBadge trajectory={s.worst_trajectory} />
                    </td>
                    {MODS.map(mod => {
                      const m = s.modalities[mod];
                      return (
                        <td key={mod} className="p-3 text-center">
                          <ProjectionArrow current={m.current} projected={m.projected_eoy} color={MOD_CFG[mod].color} />
                          {m.trajectory !== s.worst_trajectory && m.trajectory !== "growing" && m.trajectory !== "stable" && (
                            <TrajectoryBadge trajectory={m.trajectory} size="sm" />
                          )}
                        </td>
                      );
                    })}
                    <td className="p-3 text-center">
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <StudentProjectionDetail student={s} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StudentProjectionDetail = ({ student }) => {
  return (
    <div className="bg-gray-50 border-t-2 border-[#002FA7] p-5" data-testid="projection-detail">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {MODS.map(mod => {
          const cfg = MOD_CFG[mod];
          const Icon = cfg.icon;
          const m = student.modalities[mod];
          return (
            <div key={mod} className="bg-white border p-3" data-testid={`projection-detail-${mod}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Icon size={14} style={{ color: cfg.color }} />
                  <span className="text-xs font-semibold">{cfg.label}</span>
                </div>
                <TrajectoryBadge trajectory={m.trajectory} size="sm" />
              </div>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="text-[10px] text-gray-400">Current</div>
                  <div className="text-lg font-bold" style={{ color: cfg.color }}>{m.current}</div>
                </div>
                <ArrowRight size={16} className="text-gray-300" />
                <div className="text-right">
                  <div className="text-[10px] text-gray-400">Projected</div>
                  <div className="text-lg font-bold" style={{ color: cfg.color }}>{m.projected_eoy}</div>
                </div>
              </div>
              <ProjectionGauge current={m.current} projected={m.projected_eoy} goal={m.goal} color={cfg.color} />
              {m.goal && (
                <div className="mt-2 text-[10px] text-gray-500 flex items-center gap-1">
                  <Target size={9} className="text-green-600" />
                  Goal: {m.goal}
                  {m.projected_eoy >= m.goal ? (
                    <span className="text-green-600 font-bold ml-auto">Will reach goal</span>
                  ) : (
                    <span className="text-amber-600 font-bold ml-auto">Gap: {(m.goal - m.projected_eoy).toFixed(1)}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PredictiveProjections;
